import React, { useState, useEffect, useMemo } from 'react';
import api from '../api';

const TEAL   = '#14b8a6';
const AMBER  = '#f59e0b';
const RED    = '#ef4444';
const CARD   = '#1e2329';
const SUNK   = '#171b21';
const BORDER = '#2a3040';
const MUTED  = '#6b7280';
const TEXT   = '#e5e7eb';
const DIM    = '#9ca3af';
const INPUT  = '#141920';

const SHIFT_NAMES = ['morning', 'afternoon', 'night'];
const shiftListName = n => SHIFT_NAMES.slice(0, n).join(' + ');

const st = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.78)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1200, fontFamily: 'system-ui', padding: 20 },
  card:    { background: CARD, border: `1px solid ${BORDER}`, borderRadius: 16, width: '100%', maxWidth: 640, maxHeight: '90vh', display: 'flex', flexDirection: 'column' },
  head:    { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px 22px 15px', borderBottom: `1px solid ${BORDER}`, flexShrink: 0 },
  title:   { fontSize: 15, fontWeight: 700, color: TEXT, display: 'flex', alignItems: 'center', gap: 9 },
  body:    { padding: '20px 22px 24px', overflowY: 'auto', flex: 1 },
  footer:  { padding: '13px 22px', display: 'flex', gap: 8, justifyContent: 'flex-end', borderTop: `1px solid ${BORDER}`, flexShrink: 0 },
  label:   { display: 'block', marginBottom: 6, fontSize: 11, color: MUTED, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' },
  input:   { width: '100%', boxSizing: 'border-box', padding: '9px 12px', background: INPUT, border: `1px solid ${BORDER}`, borderRadius: 8, color: TEXT, fontSize: 14, outline: 'none' },
  btnPrim: { padding: '9px 20px', background: TEAL, color: '#04201d', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: 13 },
  warnBox: { border: '1px solid', borderRadius: 10, padding: '11px 13px', marginBottom: 12 },
  btnGhost:{ padding: '9px 16px', background: 'transparent', color: MUTED, border: `1px solid ${BORDER}`, borderRadius: 8, cursor: 'pointer', fontSize: 13 },
  eyebrow: { fontSize: 10, color: MUTED, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.09em' },
};

const machineName = m => m.display_name || m.name;

// "1 person", "0–1 people".
const ppl = (a, b) => (Number(a) === Number(b) ? `${a} ${Number(a) === 1 ? 'person' : 'people'}` : `${a}–${b} people`);
// A window's people per shift, in one line: "morning 1 person, afternoon 0–1 people".
const peopleLine = (people, shifts) => SHIFT_NAMES.slice(0, shifts)
  .map((name, i) => { const p = people && (people[i + 1] || people[String(i + 1)]); return p ? `${name} ${ppl(p.people_min, p.people_max)}` : null; })
  .filter(Boolean).join(', ');
const COVER_WORDS = {
  stay_longer: 'I stay longer', do_more_myself: 'I do more of the work myself', more_scrap: 'we run faster and accept more scrap',
  overtime: 'overtime for the team', on_call: 'someone on call comes in',
};

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d + 'T12:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}
function overlaps(aFrom, aTo, bFrom, bTo) {
  return aFrom <= bTo && bFrom <= aTo;
}

// Declaring that machines run extra shifts for a period.
//
// A window is a record of a decision, not an edit to jobs: it changes what the
// scheduler believes a day can make, from the moment it exists. Work already on
// the board keeps the dates it was given — replanning that is a separate job,
// and saying so plainly here stops the window promising more than it does.
export default function ShiftWindowsModal({ onClose, machines = [], onChanged, onReflow }) {
  const [windows, setWindows]   = useState([]);
  const [closures, setClosures] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState(null);
  const [creating, setCreating] = useState(false);
  const [justMade, setJustMade] = useState(null); // { ids, names, kind, days } | null

  const [picked, setPicked]     = useState([]);
  const [from, setFrom]         = useState('');
  const [to, setTo]             = useState('');
  const [shifts, setShifts]     = useState(2);
  const [nonWorking, setNonWorking] = useState(false);
  const [throughClosures, setThroughClosures] = useState(false);
  const [reason, setReason]     = useState('');

  // What the frozen zone is, known before any date is chosen rather than
  // discovered afterwards from a re-flow that moved nothing.
  const [fz, setFz] = useState(null);   // { today, working_days, frozen_until, first_free }
  const [override, setOverride] = useState(false);   // this window only; never the policy

  // "Keep these dates" acknowledges the notice and puts it away. Reset whenever
  // the dates change, because a notice about one range says nothing about the
  // next one and a dismissed warning that never returns is a warning lost.
  const [keptDates, setKeptDates] = useState(false);
  useEffect(() => { setKeptDates(false); }, [from, to]);

  // What the window costs in people (the director, 7 Oct 2026): it adds none,
  // it moves them -- "1 works in the morning and another in the afternoon".
  const [people, setPeople] = useState(null);   // { shifts, says, presets, usual } from the server
  // Who works each shift on the window's days, as the form asks it (9 Oct
  // 2026): filled in with the split, or extra people for the new shifts.
  const [plan, setPlan] = useState(null);       // { 1: { people_min, people_max, hours }, ... }
  const [preset, setPreset] = useState('split');
  useEffect(() => {
    let gone = false;
    api.get(`/api/v1/staffing/window-people?shifts=${shifts}`)
      .then(r => { if (!gone) { setPeople(r.data); setPlan(r.data.presets ? r.data.presets.split : null); setPreset('split'); } })
      .catch(() => { if (!gone) { setPeople(null); setPlan(null); } });
    return () => { gone = true; };
  }, [shifts]);
  function applyPreset(k) {
    if (!people || !people.presets) return;
    setPlan(people.presets[k]); setPreset(k);
  }
  function setPlanField(slot, field, value) {
    setPlan(p => ({ ...p, [slot]: { ...((p || {})[slot] || { people_min: 0, people_max: 0, hours: 8 }), [field]: value } }));
    setPreset('custom');
  }

  // Changing the policy itself: a different act from one window being the
  // exception, so it is a different control that says what it affects.
  const [editFz, setEditFz]     = useState(false);
  const [fzDraft, setFzDraft]   = useState('2');
  const [savingFz, setSavingFz] = useState(false);

  async function saveFrozenZone() {
    setSavingFz(true); setError(null);
    try {
      const { data } = await api.put('/api/v1/reflow/frozen-zone', { working_days: Number(fzDraft) });
      setFz(f => ({ ...(f || {}), ...data }));
      setEditFz(false);
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Could not change the frozen zone');
    } finally {
      setSavingFz(false);
    }
  }

  useEffect(() => {
    api.get('/api/v1/reflow/frozen-zone')
      .then(r => setFz(r.data))
      .catch(() => {});   // the form still works; it just cannot warn
  }, []);

  const today = fz?.today || new Date().toISOString().slice(0, 10);

  // Three distinct situations, and they call for different things.
  //
  //   backdated   — the whole window is history. A record of what happened, so
  //                 the frozen zone simply does not apply: there is nothing in
  //                 the future to disturb, and making the director offset the
  //                 dates to dodge a rule that cannot bite him is worse than
  //                 not asking at all.
  //   straddles   — part history, part intention. The frozen zone applies to
  //                 the future part only.
  //   frozen      — entirely ahead, but inside the days the floor has set up for.
  const endsInPast   = !!to && to < today;
  const startsInPast = !!from && from < today;
  const frozenUntil  = fz?.frozen_until || null;
  const hitsFrozen   = !!from && !!frozenUntil && !endsInPast && from <= frozenUntil;
  const whollyFrozen = hitsFrozen && !!to && to <= frozenUntil;

  // Keep the length the director asked for and move it to where it can be used.
  // What he is deciding is how many days of extra shifts he needs, not which
  // squares of the calendar they land on.
  const shiftedRange = useMemo(() => {
    if (!from || !to || !fz?.first_free) return null;
    const ms = 86400000;
    const span = Math.round((new Date(to + 'T12:00:00Z') - new Date(from + 'T12:00:00Z')) / ms);
    if (!Number.isFinite(span) || span < 0) return null;
    const s = fz.first_free;
    const e = new Date(new Date(s + 'T12:00:00Z').getTime() + span * ms).toISOString().slice(0, 10);
    return { from: s, to: e };
  }, [from, to, fz]);

  const live = useMemo(
    () => (machines || []).filter(m => m.status !== 'archived' && m.status !== 'decommissioned'),
    [machines]);

  async function refresh() {
    setLoading(true);
    try {
      const [w, c] = await Promise.all([
        api.get('/api/v1/shift-windows'),
        api.get('/api/v1/closures').catch(() => ({ data: [] })),
      ]);
      setWindows(Array.isArray(w.data) ? w.data : []);
      setClosures(Array.isArray(c.data) ? c.data : []);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { refresh(); }, []);

  // Rows sharing a group_id were one decision, so they are shown as one.
  const groups = useMemo(() => {
    const by = new Map();
    for (const w of windows) {
      if (!by.has(w.group_id)) by.set(w.group_id, []);
      by.get(w.group_id).push(w);
    }
    return [...by.entries()]
      .map(([group_id, rows]) => ({ group_id, rows, ...rows[0] }))
      .sort((a, b) => a.start_date.localeCompare(b.start_date));
  }, [windows]);

  // A shutdown inside the dates is its own decision and has to be named.
  const hitClosures = useMemo(() => {
    if (!from || !to) return [];
    return (closures || []).filter(c => overlaps(from, to, c.date_from, c.date_to));
  }, [closures, from, to]);

  const canSave = picked.length > 0 && from && to && to >= from && !saving;

  async function create() {
    setSaving(true); setError(null);
    try {
      const { data: made } = await api.post('/api/v1/shift-windows', {
        machine_ids: picked,
        start_date: from,
        end_date: to,
        shifts: Number(shifts),
        include_non_working: nonWorking,
        runs_through_closures: nonWorking && throughClosures,
        // The button said "I have arranged it". Until now it only hid the
        // warning, so it reported an action it had not taken -- which is worse
        // than not offering it.
        ignore_frozen_zone: hitsFrozen && override,
        reason: reason.trim() || null,
        people: plan,
      });
      const names = live.filter(m => picked.includes(m.id)).map(machineName);
      setJustMade({ ids: [...picked], names, kind: 'created', groupId: made?.group_id || null });
      setPicked([]); setFrom(''); setTo(''); setReason('');
      setNonWorking(false); setThroughClosures(false); setOverride(false);
      setCreating(false);
      await refresh();
      onChanged && onChanged();
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Could not create the window');
    } finally {
      setSaving(false);
    }
  }

  async function remove(g) {
    setError(null);
    try {
      const ids = [...new Set(g.rows.map(r => r.machine_id))];
      const names = g.rows.map(r => machineName(r.machines || {})).filter(Boolean);
      const { data } = await api.delete(`/api/v1/shift-windows/${g.group_id}`);
      await refresh();
      onChanged && onChanged();

      // Ask whether anything actually needs longer now, rather than inferring it
      // from how many rows happened to sit in the date range.
      //
      // Those are different questions and the old code answered the easy one. A
      // row only needs longer if a re-flow compressed it onto the extra shifts
      // in the first place -- and if none ever ran, nothing was compressed and
      // there is nothing to warn about. Removing a window that changed nothing
      // used to raise an alarm in red, which is worse than saying nothing.
      if (data?.rows_inside > 0 && (data.machine_ids || ids).length > 0) {
        try {
          const { data: pv } = await api.post('/api/v1/reflow/preview', {
            machine_ids: data.machine_ids || ids,
            from_date: data.start_date,
            to_date: data.end_date,
            windows_apply: true,
            pull_forward: false,
          });
          if (pv?.summary?.runs_moved > 0) {
            setJustMade({
              ids, names, kind: 'removed',
              moved: pv.summary.runs_moved,
              daysLost: Math.abs(pv.summary.days_saved || 0),
              breaks: pv.summary.breaks_deadline || [],
            });
          }
        } catch (_) {
          // If the check cannot run, say the cautious thing rather than the
          // confident one -- and never the specific one, which would be invented.
          setJustMade({ ids, names, kind: 'removed', unknown: true });
        }
      }
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Could not remove the window');
    }
  }

  return (
    <div style={st.overlay} onClick={onClose}>
      <div style={st.card} onClick={e => e.stopPropagation()}>
        <div style={st.head}>
          <div style={st.title}>
            <i className="fa-solid fa-layer-group" style={{ color: TEAL }} />
            Shift windows
          </div>
          <button aria-label="Close" style={{ background: 'none', border: 'none', color: MUTED, fontSize: 16, cursor: 'pointer' }} onClick={onClose}>✕</button>
        </div>

        <div style={st.body}>
          <div style={{ fontSize: 13, color: DIM, lineHeight: 1.6, marginBottom: 16 }}>
            A window tells the scheduler a machine works extra shifts for a period.
            Anything planned from now on uses the bigger capacity.
            <strong style={{ color: TEXT }}> Work already on the board keeps its dates.</strong>
          </div>

          {error && (
            <div style={{ marginBottom: 16, padding: '11px 14px', background: `${AMBER}12`,
                          border: `1px solid ${AMBER}44`, borderRadius: 9, fontSize: 13, color: DIM, lineHeight: 1.55 }}>
              {error}
            </div>
          )}

          {/* Shown only when work genuinely needs longer without the window.
              A removal that changes nothing produces nothing here, because an
              alarm that cries wolf is spent the first time it does. */}
          {justMade && justMade.kind === 'removed' && onReflow && (() => {
            const breaks = justMade.breaks || [];
            const c = breaks.length > 0 ? RED : AMBER;
            return (
              <div style={{ marginBottom: 16, padding: '12px 14px', background: `${c}0e`,
                            border: `1px solid ${c}40`, borderRadius: 10 }}>
                <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                  <i className="fa-solid fa-triangle-exclamation" style={{ color: c, fontSize: 13, marginTop: 3 }} />
                  <div style={{ flex: 1, minWidth: 200, fontSize: 13, color: DIM, lineHeight: 1.55 }}>
                    <strong style={{ color: c }}>Removed.</strong>{' '}
                    {justMade.unknown ? (
                      <>Capacity is back to one shift on {justMade.names.join(', ')}. I could not check
                      whether anything booked there now needs longer — worth a look.</>
                    ) : (
                      <>
                        {justMade.moved} run{justMade.moved === 1 ? '' : 's'} on these machines
                        {justMade.daysLost > 0
                          ? <> need {justMade.daysLost} more day{justMade.daysLost === 1 ? '' : 's'} now</>
                          : <> need longer now</>}
                        {' '}the extra shifts are gone. The board still shows the old dates.
                      </>
                    )}
                    {breaks.length > 0 && (
                      <div style={{ marginTop: 7, color: '#fca5a5' }}>
                        {breaks.slice(0, 3).map(b => (
                          <div key={b.plan_id}>
                            <strong style={{ color: TEXT }}>{b.delivery_week ? `Wk ${b.delivery_week} ` : ''}{b.part}</strong>
                            {' '}would miss its cut-off.
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 7, marginTop: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                  <button onClick={() => onReflow(justMade.ids, justMade.names)}
                    style={{ ...st.btnPrim, padding: '6px 13px', fontSize: 12.5,
                             ...(breaks.length > 0 ? { background: RED, color: '#fff' } : {}) }}>
                    Preview re-flow
                  </button>
                  <button onClick={() => setJustMade(null)}
                    style={{ ...st.btnGhost, padding: '6px 11px', fontSize: 12.5 }}>
                    Leave it
                  </button>
                  <span style={{ fontSize: 11.5, color: MUTED }}>
                    — the board keeps showing the dates it has
                  </span>
                </div>
              </div>
            );
          })()}

          {loading ? (
            <div style={{ color: MUTED, fontSize: 13 }}>Loading…</div>
          ) : (
            <>
              {groups.length === 0 ? (
                <div style={{ padding: '18px 16px', background: SUNK, border: `1px dashed ${BORDER}`,
                              borderRadius: 10, fontSize: 13, color: MUTED, textAlign: 'center' }}>
                  No windows declared. Every machine runs one shift.
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {groups.map(g => (
                    <React.Fragment key={g.group_id}>
                      <WindowRow g={g}
                        isNew={justMade?.kind === 'created' && justMade.groupId === g.group_id}
                        onRemove={() => remove(g)}
                        onReflow={onReflow
                          ? () => onReflow([...new Set(g.rows.map(r => r.machine_id))],
                                           g.rows.map(r => machineName(r.machines || {})).filter(Boolean),
                                           // A window that began before today is re-flowed from its own
                                           // start, so the days that actually ran extra shifts are laid
                                           // again at the capacity they really had.
                                           g.start_date < today ? g.start_date : null)
                          : null} />

                      {/* There used to be an offer here after creating a window:
                          a second Re-flow button, a "Not now", and the sentence
                          "work already booked still has the dates it was given".
                          All three were redundant. The button duplicated the one
                          on the card; "Not now" is what not clicking already
                          means; and that sentence is printed at the top of this
                          same dialog, permanently. One thing shown twice reads as
                          a choice between two things. */}
                    </React.Fragment>
                  ))}
                </div>
              )}

              {!creating ? (
                <button onClick={() => setCreating(true)}
                  style={{ ...st.btnGhost, marginTop: 16, color: TEAL, borderColor: `${TEAL}55`,
                           display: 'flex', alignItems: 'center', gap: 8 }}>
                  <i className="fa-solid fa-plus" style={{ fontSize: 11 }} />Declare a window
                </button>
              ) : (
                <div style={{ marginTop: 18, paddingTop: 16, borderTop: `1px solid ${BORDER}` }}>
                  <div style={{ ...st.eyebrow, marginBottom: 12 }}>New window</div>

                  <label style={st.label}>Machines</label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 6 }}>
                    {live.map(m => {
                      const on = picked.includes(m.id);
                      return (
                        <button key={m.id}
                          onClick={() => setPicked(p => on ? p.filter(x => x !== m.id) : [...p, m.id])}
                          style={{ padding: '5px 11px', fontSize: 12.5, borderRadius: 7, cursor: 'pointer',
                                   background: on ? `${TEAL}20` : 'transparent',
                                   border: `1px solid ${on ? TEAL : BORDER}`,
                                   color: on ? TEAL : DIM, fontWeight: on ? 700 : 400 }}>
                          {machineName(m)}
                        </button>
                      );
                    })}
                  </div>
                  <button onClick={() => setPicked(picked.length === live.length ? [] : live.map(m => m.id))}
                    style={{ background: 'none', border: 'none', color: MUTED, fontSize: 12,
                             cursor: 'pointer', padding: 0, marginBottom: 14 }}>
                    {picked.length === live.length ? 'Clear all' : 'Select all'}
                  </button>

                  {/* Said before the dates are chosen, not after a re-flow that
                      moved nothing. */}
                  {/* The policy, and a way to change it. Until now this said
                      "shorten it in settings" and there were no settings — the
                      only way was SQL. Changing it here is a separate, explicit
                      act from declaring one window as an exception to it. */}
                  {fz && !startsInPast && (
                    <div style={{ fontSize: 12, color: MUTED, marginBottom: 10, lineHeight: 1.5 }}>
                      {frozenUntil
                        ? <>The next {fz.working_days} working day{fz.working_days === 1 ? '' : 's'} are
                           frozen — work up to {fmtDate(frozenUntil)} keeps its dates, because the floor
                           has already set up for them.{' '}</>
                        : <>Nothing is frozen — re-flow may move work from tomorrow onwards.{' '}</>}
                      {editFz ? (
                        <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center', marginTop: 4 }}>
                          <input type="number" min="0" max="30" value={fzDraft}
                            onChange={e => setFzDraft(e.target.value)}
                            style={{ ...st.input, width: 62, padding: '4px 7px', fontSize: 12.5 }} />
                          <span>working days</span>
                          <button onClick={saveFrozenZone} disabled={savingFz}
                            style={{ ...st.btnPrim, padding: '4px 10px', fontSize: 12 }}>
                            {savingFz ? '…' : 'Save for everything'}
                          </button>
                          <button onClick={() => setEditFz(false)}
                            style={{ background: 'none', border: 'none', color: MUTED, fontSize: 12,
                                     cursor: 'pointer', textDecoration: 'underline', padding: 0 }}>
                            cancel
                          </button>
                        </span>
                      ) : (
                        <button onClick={() => { setFzDraft(String(fz.working_days)); setEditFz(true); }}
                          style={{ background: 'none', border: 'none', color: TEAL, fontSize: 12,
                                   cursor: 'pointer', textDecoration: 'underline', padding: 0 }}>
                          change
                        </button>
                      )}
                    </div>
                  )}

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 130px', gap: 12, marginBottom: 14 }}>
                    <div>
                      <label style={st.label} htmlFor="sw-from">From</label>
                      <input id="sw-from" style={st.input} type="date" value={from} onChange={e => setFrom(e.target.value)} />
                    </div>
                    <div>
                      <label style={st.label} htmlFor="sw-to">To</label>
                      <input id="sw-to" style={st.input} type="date" value={to} onChange={e => setTo(e.target.value)} />
                    </div>
                    <div>
                      <label style={st.label} htmlFor="sw-shifts">Shifts</label>
                      {/* The shift names live under the control, not inside it.
                          At 130px the option "2 — morning + afternoon" clipped to
                          "2 — morning", which reads as though two shifts meant
                          mornings only — the one thing this must not say. Widening
                          the column would cramp the dates; a line underneath fits
                          whatever the names are, and matches how the rest of this
                          form explains its controls. */}
                      <select id="sw-shifts" style={st.input} value={shifts} onChange={e => setShifts(Number(e.target.value))}>
                        <option value={2}>2 shifts</option>
                        <option value={3}>3 shifts</option>
                      </select>
                      <div style={{ fontSize: 11.5, color: MUTED, marginTop: 5, lineHeight: 1.4 }}>
                        {shifts === 3 ? 'Round the clock' : shiftListName(2)}
                      </div>
                    </div>
                  </div>

                  {/* Who works each shift on these days (the director, 9 Oct
                      2026): a window adds no people unless he says so. Filled
                      in with the split of the usual week, or extra people for
                      the new shifts; every number can be changed. */}
                  {plan && people && (() => {
                    const rows = [1, 2, 3].slice(0, shifts);
                    const sum = k => rows.reduce((n, sl) => n + (Number((plan[sl] || {})[k]) || 0), 0);
                    const u = people.usual || { people_min: 0, people_max: 0 };
                    const moreMin = sum('people_min') - u.people_min, moreMax = sum('people_max') - u.people_max;
                    const empty = rows.filter(sl => !(Number((plan[sl] || {}).people_max) > 0)).map(sl => SHIFT_NAMES[sl - 1]);
                    const num = { ...st.input, width: 56, padding: '6px 8px', fontSize: 13, textAlign: 'center' };
                    const chip = on => ({ padding: '5px 11px', fontSize: 12, fontWeight: on ? 700 : 500, borderRadius: 7, cursor: 'pointer',
                      border: `1px solid ${on ? TEAL : BORDER}`, background: on ? `${TEAL}18` : 'transparent', color: on ? TEAL : DIM });
                    return (
                      <div style={{ margin: '12px 0 14px', padding: '12px 14px', background: SUNK, border: `1px solid ${BORDER}`, borderRadius: 10 }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
                          <span style={st.label}><i className="fa-solid fa-user-group" style={{ color: AMBER, marginRight: 6 }} />People on these days</span>
                          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                            <button type="button" style={chip(preset === 'split')} onClick={() => applyPreset('split')}>Split my people</button>
                            <button type="button" style={chip(preset === 'extra')} onClick={() => applyPreset('extra')}>Extra people for the new shifts</button>
                          </div>
                        </div>
                        {rows.map(sl => {
                          const p = plan[sl] || { people_min: 0, people_max: 0, hours: 8 };
                          return (
                            <div key={sl} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0', flexWrap: 'wrap' }}>
                              <span style={{ width: 82, fontSize: 13, fontWeight: 600, color: TEXT, textTransform: 'capitalize' }}>{SHIFT_NAMES[sl - 1]}</span>
                              <input type="number" min={0} step={0.5} style={num} value={p.people_min} aria-label={`${SHIFT_NAMES[sl - 1]} people from`}
                                onChange={e => setPlanField(sl, 'people_min', e.target.value === '' ? 0 : Number(e.target.value))} />
                              <span style={{ fontSize: 12, color: MUTED }}>to</span>
                              <input type="number" min={0} step={0.5} style={num} value={p.people_max} aria-label={`${SHIFT_NAMES[sl - 1]} people to`}
                                onChange={e => setPlanField(sl, 'people_max', e.target.value === '' ? 0 : Number(e.target.value))} />
                              <span style={{ fontSize: 12, color: MUTED }}>people,</span>
                              <input type="number" min={1} max={12} step={0.5} style={num} value={p.hours} aria-label={`${SHIFT_NAMES[sl - 1]} hours a person`}
                                onChange={e => setPlanField(sl, 'hours', e.target.value === '' ? 8 : Number(e.target.value))} />
                              <span style={{ fontSize: 12, color: MUTED }}>h each</span>
                            </div>
                          );
                        })}
                        <div style={{ fontSize: 12, color: DIM, lineHeight: 1.5, marginTop: 8 }}>
                          {!people.configured
                            ? 'Your usual week is not set yet (Admin → Staffing); these numbers are what staffing will count on these days.'
                            : moreMax <= 0 && moreMin <= 0
                              ? `Your usual ${ppl(u.people_min, u.people_max)}, shared across the shifts, for every machine on these days.`
                              : `Your usual ${ppl(u.people_min, u.people_max)}, plus ${moreMin === moreMax ? moreMax : `${Math.max(0, moreMin)}–${moreMax}`} extra on these days.`}
                          {empty.length > 0 && (
                            <span style={{ color: AMBER }}> The {empty.join(' and ')} {empty.length === 1 ? 'has' : 'have'} nobody: anything needing attention there will show as short.</span>
                          )}
                        </div>
                      </div>
                    );
                  })()}

                  {/* One notice, not two.
                      A window from 21 to 25 declared on the 23rd is part past and
                      part frozen, and both facts mean the same thing: none of
                      these dates can move existing work. Said once, with the
                      escape offered once.

                      Only what is true appears. A window entirely in the past
                      says nothing about the frozen zone, which cannot apply to
                      days already worked, and offers no override because there is
                      nothing to override. */}
                  {(startsInPast || hitsFrozen) && !keptDates && !(hitsFrozen && override && !startsInPast) && (() => {
                    const pastPart   = startsInPast;
                    const frozenPart = hitsFrozen && !endsInPast;
                    const title = pastPart
                      ? 'This window covers days already worked'
                      : whollyFrozen
                        ? 'This whole window is inside the frozen zone'
                        : 'It starts inside the frozen zone';
                    return (
                      <div style={{ ...st.warnBox, borderColor: `${AMBER}55`, background: `${AMBER}0e` }}>
                        <div style={{ color: AMBER, fontWeight: 700, fontSize: 13, marginBottom: 5 }}>{title}</div>
                        <div style={{ fontSize: 12.5, color: DIM, lineHeight: 1.55 }}>
                          {pastPart
                            ? <>Those days ran extra shifts, so work across them took fewer days than the
                               board shows. Re-flow corrects it and pulls later work forward. Runs already
                               started or finished keep their dates.</>
                            : <>Work up to {fmtDate(frozenUntil)} keeps its dates, so nothing already booked
                               would move. It would still apply to anything scheduled from now on.</>}
                        </div>
                        <div style={{ display: 'flex', gap: 7, marginTop: 9, flexWrap: 'wrap', alignItems: 'center' }}>
                          {shiftedRange && (
                            <button onClick={() => { setFrom(shiftedRange.from); setTo(shiftedRange.to); }}
                              style={{ ...st.btnPrim, fontSize: 12.5, padding: '5px 11px' }}>
                              Same length from {fmtDate(shiftedRange.from)}
                            </button>
                          )}
                          {frozenPart && (
                            <button onClick={() => setOverride(true)}
                              style={{ ...st.btnGhost, fontSize: 12.5, padding: '5px 11px' }}>
                              Override — I've arranged it
                            </button>
                          )}
                          {/* A real third option rather than a sentence to read.
                              Doing nothing IS the choice most of the time, and it
                              should look like one — outlined rather than filled,
                              so it reads as available without competing with the
                              action that changes something. */}
                          <button onClick={() => setKeptDates(true)}
                            style={{ ...st.btnGhost, fontSize: 12.5, padding: '5px 11px',
                                     borderColor: MUTED, color: DIM }}>
                            Keep these dates
                          </button>
                        </div>
                      </div>
                    );
                  })()}

                  {hitsFrozen && override && (
                    <div style={{ fontSize: 12.5, color: AMBER, marginBottom: 12, lineHeight: 1.5 }}>
                      Frozen zone overridden for this window.{' '}
                      <button onClick={() => setOverride(false)}
                        style={{ background: 'none', border: 'none', color: MUTED, textDecoration: 'underline',
                                 cursor: 'pointer', fontSize: 12.5, padding: 0 }}>
                        undo
                      </button>
                    </div>
                  )}


                  <label style={{ display: 'flex', alignItems: 'flex-start', gap: 9, cursor: 'pointer',
                                  fontSize: 13, color: DIM, lineHeight: 1.5, marginBottom: 12 }}>
                    <input type="checkbox" checked={nonWorking}
                           onChange={e => { setNonWorking(e.target.checked); if (!e.target.checked) setThroughClosures(false); }}
                           style={{ accentColor: TEAL, width: 15, height: 15, marginTop: 1, flexShrink: 0, cursor: 'pointer' }} />
                    <span>
                      Work weekends and public holidays
                      <span style={{ display: 'block', fontSize: 12, color: MUTED }}>
                        Otherwise non-working days stay as they are.
                      </span>
                    </span>
                  </label>

                  {/* A shutdown is a different order of decision, so it is named
                      and confirmed on its own rather than swept up by the tick above. */}
                  {nonWorking && hitClosures.length > 0 && (
                    <div style={{ padding: '12px 14px', background: `${RED}10`, border: `1px solid ${RED}44`,
                                  borderRadius: 9, marginBottom: 12 }}>
                      <div style={{ fontSize: 12.5, color: DIM, lineHeight: 1.55, marginBottom: 8 }}>
                        This window overlaps{' '}
                        {hitClosures.map((c, i) => (
                          <span key={c.id || i}>
                            {i > 0 && ', '}
                            <strong style={{ color: TEXT }}>{c.reason || c.name || 'a closure'}</strong>
                            {' '}({fmtDate(c.date_from)} – {fmtDate(c.date_to)})
                          </span>
                        ))}.
                      </div>
                      <label style={{ display: 'flex', alignItems: 'flex-start', gap: 9, cursor: 'pointer',
                                      fontSize: 13, color: DIM, lineHeight: 1.5 }}>
                        <input type="checkbox" checked={throughClosures}
                               onChange={e => setThroughClosures(e.target.checked)}
                               style={{ accentColor: RED, width: 15, height: 15, marginTop: 1, flexShrink: 0, cursor: 'pointer' }} />
                        <span>Run through it — staffing is arranged</span>
                      </label>
                    </div>
                  )}

                  <label style={st.label} htmlFor="sw-reason">Why</label>
                  <input id="sw-reason" style={{ ...st.input, marginBottom: 14 }} value={reason} maxLength={120}
                         onChange={e => setReason(e.target.value)} placeholder="e.g. Müller order, Wk 39" />

                  <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={() => setCreating(false)} style={st.btnGhost}>Cancel</button>
                    <button onClick={create} disabled={!canSave}
                      style={{ ...st.btnPrim, opacity: canSave ? 1 : 0.4, cursor: canSave ? 'pointer' : 'not-allowed' }}>
                      {saving ? 'Saving…' : 'Declare window'}
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div style={st.footer}>
          <button style={st.btnGhost} onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

function WindowRow({ g, onRemove, onReflow, isNew }) {
  const names = g.rows.map(r => machineName(r.machines || {})).filter(Boolean);
  return (
    <div style={{ padding: '12px 14px', background: SUNK,
                  border: `1px solid ${isNew ? TEAL : BORDER}`, borderRadius: 10,
                  display: 'flex', gap: 12, alignItems: 'flex-start' }}>
      <div style={{ width: 26, height: 26, borderRadius: 7, background: `${TEAL}1c`, border: `1px solid ${TEAL}3a`,
                    display: 'flex', flexDirection: 'column', gap: 1.5, alignItems: 'center',
                    justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>
        {Array.from({ length: Math.max(1, g.shifts - 1) }, (_, k) => (
          <div key={k} style={{ width: 12, height: 2.5, borderRadius: 2, background: TEAL, opacity: k === 0 ? 0.9 : 0.55 }} />
        ))}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13.5, fontWeight: 700, color: TEXT, display: 'flex',
                      alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span>{g.shifts} shifts · {names.join(', ') || 'machine'}</span>
          {isNew && (
            <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.05em',
                           textTransform: 'uppercase', padding: '2px 7px', borderRadius: 4,
                           background: `${TEAL}1c`, color: TEAL, border: `1px solid ${TEAL}55` }}>
              just declared
            </span>
          )}
        </div>
        <div style={{ fontSize: 12.5, color: DIM, marginTop: 2 }}>
          {fmtDate(g.start_date)} – {fmtDate(g.end_date)}
          {g.include_non_working && <span style={{ color: AMBER }}> · incl. weekends</span>}
          {g.runs_through_closures && <span style={{ color: RED }}> · through a closure</span>}
        </div>
        {g.reason && <div style={{ fontSize: 12.5, color: MUTED, marginTop: 3 }}>{g.reason}</div>}
        {g.people && (
          <div style={{ fontSize: 12, color: DIM, marginTop: 3 }}>
            <i className="fa-solid fa-user-group" style={{ fontSize: 10, color: AMBER, marginRight: 5 }} />
            {peopleLine(g.people, g.shifts)}
          </div>
        )}
        {(() => {
          const covers = Array.isArray(g.staffing_cover) ? g.staffing_cover : (g.staffing_cover ? [g.staffing_cover] : []);
          if (!covers.length) return null;
          const n = covers.reduce((k, c) => k + (c.shifts || []).length, 0);
          const last = covers[covers.length - 1];
          const how = [(last.how || []).map(k => COVER_WORDS[k]).filter(Boolean).join('; '), last.note ? `“${last.note}”` : ''].filter(Boolean).join(' — ');
          return (
            <div style={{ fontSize: 12, color: DIM, marginTop: 3 }}>
              <i className="fa-solid fa-shield-halved" style={{ fontSize: 10, color: TEAL, marginRight: 5 }} />
              Covered by {last.by || 'you'}: {n} shift{n === 1 ? '' : 's'}{how ? ` — ${how}` : ''}
            </div>
          );
        })()}
        {g.created_by && (
          <div style={{ fontSize: 11.5, color: MUTED, marginTop: 4 }}>
            declared by {g.created_by}{g.created_at ? ` · ${fmtDate(g.created_at.slice(0, 10))}` : ''}
          </div>
        )}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 5, flexShrink: 0 }}>
        {/* "Preview" because it opens something to look at. "Re-flow" on its own
            reads like it does it, and nothing about the button said the
            confirmation arrives on the next screen. */}
        {onReflow && (
          <button onClick={onReflow} title="See what would move, before anything does"
            style={{ background: TEAL, border: 'none', color: '#04201d', borderRadius: 7,
                     padding: '5px 12px', fontSize: 11.5, fontWeight: 700, cursor: 'pointer',
                     whiteSpace: 'nowrap' }}>
            Preview re-flow
          </button>
        )}
        <button onClick={onRemove} title="Remove this window"
          style={{ background: 'none', border: `1px solid ${BORDER}`, color: MUTED, borderRadius: 7,
                   padding: '5px 10px', fontSize: 11.5, cursor: 'pointer' }}>
          Remove
        </button>
      </div>
    </div>
  );
}
